import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Button, Typography, Space, List, Tag, Empty, Spin, Alert, Statistic, Row, Col, message,
} from 'antd';
import {
  ArrowLeftOutlined, CarOutlined, ScheduleOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import {
  fetchServiceTable, sortByUrgency, STATUS_PRESENTATION, ServiceTable, ServiceDueRow,
} from '../services/serviceTableApi';

const { Title, Text } = Typography;

/**
 * WP-13 slice 4 — "Service schedule": what this vehicle needs and when.
 *
 * The whole design problem of this screen is that four of the seven statuses are
 * NOT answers — they are admissions that the shop does not have enough
 * information. Those must be legible as such at a glance and must never be
 * mistaken for "you're fine". So:
 *
 *   - they render in neutral grey, borrowing neither the green of "upcoming" nor
 *     the red of "overdue";
 *   - they are sorted BELOW the real answers, so the top of the list is always
 *     the things a customer can act on;
 *   - each one states what is missing in a sentence, rather than showing a blank
 *     cell the customer has to interpret.
 *
 * An AI-suggested interval gets an extra, explicit line. It is the one case
 * where a number exists and is deliberately not being used, and saying so is
 * more honest than quietly omitting the row.
 */
const CustomerServiceTable: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [table, setTable] = useState<ServiceTable | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        setTable(await fetchServiceTable(Number(id)));
      } catch {
        message.error('Could not load the service schedule for this vehicle.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isAuthenticated, navigate]);

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  const rows = table ? sortByUrgency(table.services) : [];
  const v = table?.vehicle;

  const renderRow = (row: ServiceDueRow) => {
    const presentation = STATUS_PRESENTATION[row.status];
    const unknown = presentation.tone === 'unknown';

    return (
      <List.Item key={`${row.template_id ?? 'v'}-${row.override_id ?? 'x'}-${row.name}`}>
        <List.Item.Meta
          title={
            <Space wrap size={6}>
              <Text strong>{row.name}</Text>
              <Tag color={presentation.color}>{presentation.label}</Tag>
              {row.override_id != null && (
                <Tag color="blue">Set for your vehicle</Tag>
              )}
            </Space>
          }
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {/* The due figure, when there is one. */}
              {row.due_at_miles != null && (
                <Text type={unknown ? 'secondary' : undefined}>
                  Due at {row.due_at_miles.toLocaleString()} mi
                  {row.reason ? ` — ${row.reason}` : ''}
                </Text>
              )}
              {row.due_at_miles == null && row.due_at_date != null && (
                <Text>
                  Due {dayjs(row.due_at_date).format('MMM D, YYYY')}
                  {row.reason ? ` — ${row.reason}` : ''}
                </Text>
              )}

              {/* The honest non-answers say what is missing, in words. */}
              {unknown && <Text type="secondary">{row.reason}</Text>}

              {/* The one case where a number exists and is deliberately unused. */}
              {row.status === 'needs_review' && (
                <Text type="secondary" italic style={{ fontSize: 12 }}>
                  Suggested interval
                  {row.interval_miles ? `: every ${row.interval_miles.toLocaleString()} mi` : ''}
                  {' '}— not used until the shop confirms it.
                </Text>
              )}

              {row.last_performed_at && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last done {dayjs(row.last_performed_at).format('MMM D, YYYY')}
                  {row.last_performed_mileage != null
                    ? ` at ${row.last_performed_mileage.toLocaleString()} mi`
                    : ' (no odometer reading recorded)'}
                </Text>
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };

  return (
    <PublicLayout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
          padding: '40px 20px',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/vehicles')}
                style={{ padding: 0, marginBottom: 8 }}
              >
                Back to My Vehicles
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                <ScheduleOutlined /> Service schedule
              </Title>
              {v && (
                <Text type="secondary">
                  <CarOutlined /> {v.year} {v.make} {v.model}
                  {v.drive_type ? ` — ${v.drive_type}` : ''}
                </Text>
              )}
            </Space>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={12}>
                <Statistic
                  title="Current mileage"
                  value={
                    table?.current_mileage != null
                      ? table.current_mileage.toLocaleString()
                      : 'Not recorded'
                  }
                  suffix={table?.current_mileage != null ? 'mi' : undefined}
                />
              </Col>
            </Row>
          </Card>

          {/* A rollback means the odometer history is internally inconsistent.
              Better to say so than to present derived numbers as if they were
              solid. */}
          {table?.mileage_log_has_rollback && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 24 }}
              message="This vehicle's odometer history is inconsistent"
              description={
                'A later reading is lower than an earlier one — often a replaced ' +
                'instrument cluster or a mistyped reading. The mileages below are ' +
                'based on the most recent reading; please confirm them with the shop.'
              }
            />
          )}

          {table?.current_mileage == null && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
              message="We do not have a mileage reading for this vehicle yet"
              description={
                'Mileage-based services cannot be worked out until the shop records ' +
                'an odometer reading. Nothing below is a guess.'
              }
            />
          )}

          <Card>
            {rows.length > 0 ? (
              <List dataSource={rows} renderItem={renderRow} />
            ) : (
              <Empty
                description="Your shop has not set up a service schedule yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '60px 20px' }}
              />
            )}
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerServiceTable;
